<template>
  <div class="bg-[#f6f1ea] text-[#1a1714]">
    <!-- Dark Hero -->
    <section class="bg-[#0c0a09] text-[#f3ede3]">
      <div class="mx-auto max-w-[1680px] px-6 pb-14 pt-32 sm:px-8 lg:px-14 lg:pb-20">
        <p class="text-[0.62rem] uppercase tracking-[0.36em] text-[#f3ede3]/40">Kendrick Paris</p>
        <h1 class="mt-5 font-['Playfair_Display'] text-[3rem] leading-[0.9] tracking-[-0.05em] text-[#fbf5eb] sm:text-[4rem] lg:text-[5rem]">
          {{ copy.title }}
        </h1>
        <p class="mt-6 max-w-[36rem] text-[0.95rem] leading-[1.8] text-[#f3ede3]/50">{{ copy.subtitle }}</p>
      </div>
    </section>

    <div class="mx-auto max-w-[1680px] px-6 pt-6 sm:px-8 lg:px-14">
      <PageBreadcrumb :paths="[{ url: localizedPressPath, label: copy.title }]" />
    </div>

    <!-- Press Entries -->
    <article class="mx-auto max-w-[1680px] px-6 py-16 sm:px-8 lg:px-14 lg:py-24">
      <div class="mx-auto max-w-[64rem] space-y-24">

        <!-- Vanity Fair -->
        <div class="grid lg:grid-cols-[1fr_1fr] lg:gap-16 items-center">
          <div>
            <NuxtLink :to="productLink('ankle-boots', 'black')" :aria-label="copy.aria.blackAnkleBoots" class="block overflow-hidden rounded-sm">
              <NuxtPicture
                alt="Kendrick High Heels — Vanity Fair"
                loading="lazy"
                src="/k/kendrick-paris/medias/vanityfair/kendrick-bottines-noires-vanity-fair.png"
                class="w-full transition duration-700 hover:scale-[1.02]"
              />
            </NuxtLink>
          </div>
          <div class="mt-8 lg:mt-0">
            <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#1a1714]/40">Paris</p>
            <h2 class="mt-3 font-['Playfair_Display'] text-[2rem] leading-[1.1] tracking-[-0.03em] text-[#1a1714]">Vanity Fair</h2>
            <p class="mt-4 text-[0.95rem] leading-[1.8] text-[#1a1714]/70">
              {{ isFrench
                ? "Pour l'hiver, deux pièces noires : à vos pieds avec ces bottines Kendrick qui cochent toutes les cases."
                : isJapanese
                  ? '冬のための二つの黒。足元には、すべての条件を満たすKendrickのアンクルブーツ。'
                : 'For winter, two black pieces: on your feet with these Kendrick ankle boots that tick all the boxes.'
              }}
            </p>
            <NuxtLink
              to="https://www.vanityfair.fr/galerie/noel-2023-la-wishlist-de-la-redaction"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-6 inline-block rounded-full border border-[#1a1714]/20 px-6 py-3 text-[0.72rem] uppercase tracking-[0.28em] text-[#1a1714] transition hover:bg-[#1a1714] hover:text-[#f6f1ea]"
            >{{ copy.readArticle }}</NuxtLink>
          </div>
        </div>

        <div class="mx-auto h-px w-16 bg-[#1a1714]/10"></div>

        <!-- Madame Figaro -->
        <div class="grid lg:grid-cols-[1fr_1fr] lg:gap-16 items-center">
          <div class="order-2 lg:order-1 mt-8 lg:mt-0">
            <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#1a1714]/40">Paris</p>
            <h2 class="mt-3 font-['Playfair_Display'] text-[2rem] leading-[1.1] tracking-[-0.03em] text-[#1a1714]">Madame Figaro</h2>
            <p class="mt-4 text-[0.95rem] leading-[1.8] text-[#1a1714]/70">{{ copy.entries.madameFigaro }}</p>
          </div>
          <div class="order-1 lg:order-2 grid grid-cols-2 gap-4">
            <NuxtLink :to="productLink('ankle-boots', 'yellow')" :aria-label="copy.aria.yellowAnkleBoots" class="block overflow-hidden rounded-sm">
              <NuxtPicture
                alt="Kendrick Madame Figaro Benude"
                loading="lazy"
                src="/k/kendrick-paris/medias/madamefigaro/madamefigaro_kendrick_benude.jpg"
                class="w-full transition duration-700 hover:scale-[1.02]"
              />
            </NuxtLink>
            <NuxtLink :to="productLink('ankle-boots', 'yellow')" :aria-label="copy.aria.yellowAnkleBoots" class="block overflow-hidden rounded-sm">
              <NuxtPicture
                alt="Kendrick Madame Figaro WSN Première Classe"
                loading="lazy"
                src="/k/kendrick-paris/medias/madamefigaro/kendrick-madame-figaro-wsn-premiere-classe.jpg"
                class="w-full transition duration-700 hover:scale-[1.02]"
              />
            </NuxtLink>
          </div>
        </div>

        <div class="mx-auto h-px w-16 bg-[#1a1714]/10"></div>

        <!-- L'Officiel -->
        <div class="grid lg:grid-cols-[1fr_1fr] lg:gap-16 items-center">
          <div>
            <NuxtLink :to="productLink('ankle-boots', 'black')" :aria-label="copy.aria.blackAnkleBoots" class="block overflow-hidden rounded-sm">
              <NuxtPicture
                alt="Kendrick L'Officiel Lithuania"
                loading="lazy"
                src="/k/kendrick-paris/medias/lofficiel/kendrick-paris-lofficiel-lithuania.jpg"
                class="w-full transition duration-700 hover:scale-[1.02]"
              />
            </NuxtLink>
          </div>
          <div class="mt-8 lg:mt-0">
            <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#1a1714]/40">Lithuania</p>
            <h2 class="mt-3 font-['Playfair_Display'] text-[2rem] leading-[1.1] tracking-[-0.03em] text-[#1a1714]">L'Officiel</h2>
            <p class="mt-4 text-[0.95rem] leading-[1.8] text-[#1a1714]/70">
              {{ copy.entries.lofficiel }}
            </p>
          </div>
        </div>

        <div class="mx-auto h-px w-16 bg-[#1a1714]/10"></div>

        <!-- Première Dame -->
        <div class="grid lg:grid-cols-[1fr_1fr] lg:gap-16 items-center">
          <div class="order-2 lg:order-1 mt-8 lg:mt-0">
            <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#1a1714]/40">Paris</p>
            <h2 class="mt-3 font-['Playfair_Display'] text-[2rem] leading-[1.1] tracking-[-0.03em] text-[#1a1714]">Première Dame</h2>
            <p class="mt-4 text-[0.95rem] leading-[1.8] text-[#1a1714]/70">
              {{ copy.entries.premiereDame }}
            </p>
          </div>
          <div class="order-1 lg:order-2">
            <NuxtLink :to="productLink('ankle-boots', 'red')" :aria-label="copy.aria.redAnkleBoots" class="block overflow-hidden rounded-sm">
              <NuxtPicture
                alt="Kendrick Première Dame"
                loading="lazy"
                src="/k/kendrick-paris/medias/premieredame/premieredame_kendrick.jpg"
                class="w-full transition duration-700 hover:scale-[1.02]"
              />
            </NuxtLink>
          </div>
        </div>

        <div class="mx-auto h-px w-16 bg-[#1a1714]/10"></div>

        <!-- Palace Costes -->
        <div class="grid lg:grid-cols-[1fr_1fr] lg:gap-16 items-center">
          <div>
            <NuxtLink :to="productLink('ankle-boots', 'black')" :aria-label="copy.aria.blackAnkleBoots" class="block overflow-hidden rounded-sm">
              <NuxtPicture
                alt="Kendrick Palace Costes Fashion Week"
                loading="lazy"
                src="/k/kendrick-paris/medias/palacecostes/palacecostes_fashionweek_kendrick.jpg"
                class="w-full transition duration-700 hover:scale-[1.02]"
              />
            </NuxtLink>
          </div>
          <div class="mt-8 lg:mt-0">
            <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#1a1714]/40">Paris</p>
            <h2 class="mt-3 font-['Playfair_Display'] text-[2rem] leading-[1.1] tracking-[-0.03em] text-[#1a1714]">Palace Costes</h2>
            <p class="mt-4 text-[0.95rem] leading-[1.8] text-[#1a1714]/70">
              {{ copy.entries.palaceCostes }}
            </p>
          </div>
        </div>

        <div class="mx-auto h-px w-16 bg-[#1a1714]/10"></div>

        <!-- Palace Scope -->
        <div class="grid lg:grid-cols-[1fr_1fr] lg:gap-16 items-center">
          <div class="order-2 lg:order-1 mt-8 lg:mt-0">
            <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#1a1714]/40">Paris</p>
            <h2 class="mt-3 font-['Playfair_Display'] text-[2rem] leading-[1.1] tracking-[-0.03em] text-[#1a1714]">Palace Scope</h2>
            <p class="mt-4 text-[0.95rem] leading-[1.8] text-[#1a1714]/70">{{ copy.entries.palaceScope }}</p>
          </div>
          <div class="order-1 lg:order-2">
            <NuxtLink :to="productLink('ankle-boots', 'black')" :aria-label="copy.aria.blackAnkleBoots" class="block overflow-hidden rounded-sm">
              <NuxtPicture
                alt="Kendrick Palace Scope"
                loading="lazy"
                src="/medias/press/palacescope/palacescope_kendrick.jpg"
                class="w-full transition duration-700 hover:scale-[1.02]"
              />
            </NuxtLink>
          </div>
        </div>

        <div class="mx-auto h-px w-16 bg-[#1a1714]/10"></div>

        <!-- Livid -->
        <div class="grid lg:grid-cols-[1fr_1fr] lg:gap-16 items-center">
          <div>
            <NuxtLink :to="productLink('ankle-boots', 'red')" :aria-label="copy.aria.redAnkleBoots" class="block overflow-hidden rounded-sm">
              <NuxtPicture
                alt="Kendrick Livid New York Editorial"
                loading="lazy"
                src="/k/kendrick-paris/medias/livid/livid_newyork_editorial_kendrick.jpg"
                class="w-full transition duration-700 hover:scale-[1.02]"
              />
            </NuxtLink>
          </div>
          <div class="mt-8 lg:mt-0">
            <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#1a1714]/40">New York</p>
            <h2 class="mt-3 font-['Playfair_Display'] text-[2rem] leading-[1.1] tracking-[-0.03em] text-[#1a1714]">Livid</h2>
            <p class="mt-4 text-[0.95rem] leading-[1.8] text-[#1a1714]/70">{{ copy.entries.livid }}</p>
          </div>
        </div>

        <div class="mx-auto h-px w-16 bg-[#1a1714]/10"></div>

        <!-- Basic Magazine -->
        <div class="grid lg:grid-cols-[1fr_1fr] lg:gap-16 items-center">
          <div class="order-2 lg:order-1 mt-8 lg:mt-0">
            <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#1a1714]/40">Los Angeles</p>
            <h2 class="mt-3 font-['Playfair_Display'] text-[2rem] leading-[1.1] tracking-[-0.03em] text-[#1a1714]">Basic Magazine</h2>
            <p class="mt-4 text-[0.95rem] leading-[1.8] text-[#1a1714]/70">{{ copy.entries.basic }}</p>
          </div>
          <div class="order-1 lg:order-2">
            <NuxtLink :to="productLink('ankle-boots', 'beige')" :aria-label="copy.aria.beigeAnkleBoots" class="block overflow-hidden rounded-sm">
              <NuxtPicture
                alt="Kendrick Basic Magazine Los Angeles Cover"
                loading="lazy"
                src="/k/kendrick-paris/medias/basicmagazine/basic_magazine_los_angeles_kendrick_cover.jpg"
                class="w-full transition duration-700 hover:scale-[1.02]"
              />
            </NuxtLink>
          </div>
        </div>

        <div class="mx-auto h-px w-16 bg-[#1a1714]/10"></div>

        <!-- Arabian Moda -->
        <div class="grid lg:grid-cols-[1fr_1fr] lg:gap-16 items-center">
          <div>
            <NuxtLink :to="productLink('ankle-boots', 'red')" :aria-label="copy.aria.redAnkleBoots" class="block overflow-hidden rounded-sm">
              <NuxtPicture
                alt="Kendrick Arabian Moda"
                loading="lazy"
                src="/k/kendrick-paris/medias/arabian-moda/kendrick-arabian-moda.jpg"
                class="w-full transition duration-700 hover:scale-[1.02]"
              />
            </NuxtLink>
          </div>
          <div class="mt-8 lg:mt-0">
            <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#1a1714]/40">Dubai</p>
            <h2 class="mt-3 font-['Playfair_Display'] text-[2rem] leading-[1.1] tracking-[-0.03em] text-[#1a1714]">Arabian Moda</h2>
            <p class="mt-4 text-[0.95rem] leading-[1.8] text-[#1a1714]/70">
              {{ copy.entries.arabianModa }}
            </p>
          </div>
        </div>

        <div class="mx-auto h-px w-16 bg-[#1a1714]/10"></div>

        <!-- Purple Haze Magazine -->
        <div class="grid lg:grid-cols-[1fr_1fr] lg:gap-16 items-center">
          <div class="order-2 lg:order-1 mt-8 lg:mt-0">
            <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#1a1714]/40">International</p>
            <h2 class="mt-3 font-['Playfair_Display'] text-[2rem] leading-[1.1] tracking-[-0.03em] text-[#1a1714]">Purple Haze Magazine</h2>
            <p class="mt-4 text-[0.95rem] leading-[1.8] text-[#1a1714]/70">{{ copy.entries.purpleHaze }}</p>
            <NuxtLink
              to="https://purplehazemag.com/category/fashion/page/9/"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-6 inline-block rounded-full border border-[#1a1714]/20 px-6 py-3 text-[0.72rem] uppercase tracking-[0.28em] text-[#1a1714] transition hover:bg-[#1a1714] hover:text-[#f6f1ea]"
            >{{ copy.readArticle }}</NuxtLink>
          </div>
          <div class="order-1 lg:order-2">
            <NuxtLink
              to="https://purplehazemag.com/category/fashion/page/9/"
              target="_blank"
              rel="noopener noreferrer"
              class="block overflow-hidden rounded-sm"
            >
              <NuxtPicture
                alt="Kendrick Purple Haze Magazine"
                loading="lazy"
                src="/medias/press/purplehaze/purplehazemagazine.png"
                class="w-full transition duration-700 hover:scale-[1.02]"
              />
            </NuxtLink>
          </div>
        </div>

      </div>
    </article>

    <!-- Logos Section -->
    <section class="bg-[#0c0a09] py-20">
      <div class="mx-auto max-w-[1680px] px-6 sm:px-8 lg:px-14">
        <LogosSection />
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { buildProductColorPath } from '~/utils/colorRoute'
import {
  getLocaleLanguage,
  getLocalePrefix,
  localizePath
} from '~/utils/locales'

definePageMeta({ layout: 'editorial' })

const siteLocale = useSiteLocale()
const isFrench = computed(() => siteLocale.value === 'fr')
const isJapanese = computed(() => siteLocale.value === 'ja')
const localePrefix = computed(() => getLocalePrefix(siteLocale.value))
const localizedPressPath = computed(() => localizePath('/press', siteLocale.value))
const productLink = (productType, color) => buildProductColorPath(`/${productType}`, color, localePrefix.value)

const copy = computed(() => {
  if (isFrench.value) {
    return {
      title: 'Presse',
      subtitle: 'Couverture presse et apparitions médiatiques dans des publications du monde entier.',
      readArticle: "Lire l'article",
      entries: {
        madameFigaro: 'Madame Figaro met en avant notre dernière collection dans ses pages prestigieuses, avec notamment Benude et WSN Première Classe.',
        lofficiel: "L'Officiel Lithuania — Éditorial mode, 2023",
        premiereDame: 'Paru dans Première Dame, 2023',
        palaceCostes: 'Palace Costes — Fashion Week de Paris, 2023',
        palaceScope: 'Palace Scope présente Kendrick dans ses pages lifestyle, mode, art et création à Paris.',
        livid: "L'éditorial new-yorkais du magazine Livid capture le style international et l'attrait contemporain de Kendrick.",
        basic: 'Basic Magazine Los Angeles affiche Kendrick en couverture et souligne notre présence sur la côte Ouest.',
        arabianModa: 'Paru dans Arabian Moda, 2023',
        purpleHaze: 'Purple Haze Magazine présente Kendrick dans son éditorial mode avec des gants, bottines noires, escarpins jaunes et escarpins rouges.'
      },
      aria: {
        blackAnkleBoots: 'Voir les bottines noires Kendrick',
        yellowAnkleBoots: 'Voir les bottines jaunes Kendrick',
        redAnkleBoots: 'Voir les bottines rouges Kendrick',
        beigeAnkleBoots: 'Voir les bottines beiges Kendrick'
      }
    }
  }

  if (isJapanese.value) {
    return {
      title: 'プレス',
      subtitle: '世界各地の出版物に掲載されたKendrickのプレス掲載とメディア露出。',
      readArticle: '記事を読む',
      entries: {
        madameFigaro: 'Madame Figaroは、BenudeやWSN Première Classeを含む名高いページでKendrickの最新コレクションを紹介しています。',
        lofficiel: "L'Officiel Lithuania — ファッションエディトリアル、2023年",
        premiereDame: 'Première Dame掲載、2023年',
        palaceCostes: 'Palace Costes — パリ・ファッションウィーク、2023年',
        palaceScope: 'Palace Scopeは、パリのライフスタイル、ファッション、アート、クリエーションのページでKendrickを紹介しています。',
        livid: 'Livid magazineのニューヨークエディトリアルは、Kendrickの国際的なスタイルと現代的な魅力を捉えています。',
        basic: 'Basic Magazine Los AngelesはKendrickを表紙に掲載し、西海岸での存在感を伝えています。',
        arabianModa: 'Arabian Moda掲載、2023年',
        purpleHaze: 'Purple Haze Magazineは、手袋、黒いブーツ、黄色いパンプス、赤いパンプスを含むファッションエディトリアルでKendrickを紹介しています。'
      },
      aria: {
        blackAnkleBoots: 'Kendrickの黒いアンクルブーツを見る',
        yellowAnkleBoots: 'Kendrickの黄色いアンクルブーツを見る',
        redAnkleBoots: 'Kendrickの赤いアンクルブーツを見る',
        beigeAnkleBoots: 'Kendrickのベージュのアンクルブーツを見る'
      }
    }
  }

  return {
    title: 'Press',
    subtitle: 'Press coverage and media appearances in publications worldwide.',
    readArticle: 'Read Article',
    entries: {
      madameFigaro: "Madame Figaro features our latest collection in their prestigious pages, including coverage from Benude and WSN Première Classe.",
      lofficiel: "L'Officiel Lithuania — Fashion editorial, 2023",
      premiereDame: 'Featured in Première Dame, 2023',
      palaceCostes: 'Palace Costes — Paris Fashion Week, 2023',
      palaceScope: 'Palace Scope features Kendrick in their lifestyle, fashion, art and creation pages in Paris.',
      livid: "Livid magazine's New York editorial captures Kendrick's international style and contemporary appeal.",
      basic: 'Basic Magazine Los Angeles features Kendrick on their cover, highlighting our West Coast presence.',
      arabianModa: 'Featured in Arabian Moda, 2023',
      purpleHaze: 'Purple Haze Magazine features Kendrick in their fashion editorial with gloves, black boots, yellow pumps and red pumps.'
    },
    aria: {
      blackAnkleBoots: 'View Kendrick black ankle boots',
      yellowAnkleBoots: 'View Kendrick yellow ankle boots',
      redAnkleBoots: 'View Kendrick red ankle boots',
      beigeAnkleBoots: 'View Kendrick beige ankle boots'
    }
  }
})

useHead(() => ({
  htmlAttrs: { lang: getLocaleLanguage(siteLocale.value) },
  title: `${copy.value.title} — Kendrick Paris`,
  meta: [
    {
      name: 'description',
      content: copy.value.subtitle
    }
  ],
  link: [{ rel: 'canonical', href: `https://kendrick.paris${localizedPressPath.value}` }]
}))
</script>

<style scoped>
/* editorial — utility-only page */
</style>
